export enum UpscaleMethod {
  REPEAT,
  SCALE,
}

export const TEXTURE_MIGRATION_TARGET_RESOLUTION = 1024;
export const REPEATING_TEXTURE_MIGRATION_SCALE_FACTOR = TEXTURE_MIGRATION_TARGET_RESOLUTION / 512;

export interface PackedTextureMigrationWithMetadata {
  texturePackedBaseName: string;
  upscaleMethodRed?: UpscaleMethod;
  upscaleMethodGreen?: UpscaleMethod;
  upscaleMethodBlue?: UpscaleMethod;
  overrideRedName?: string;
  overrideGreenName?: string;
  overrideBlueName?: string;
  replaceRedWith?: string;
  replaceBlueWith?: string;
  replaceGreenWith?: string;
}

interface ChannelUpscaleMethods {
  upscaleMethodRed: UpscaleMethod;
  upscaleMethodGreen: UpscaleMethod;
  upscaleMethodBlue: UpscaleMethod;
}

export type PackedTextureMigration = Omit<PackedTextureMigrationWithMetadata, "texturePackedBaseName"> &
  ChannelUpscaleMethods;

export const defaultPackedTextureMigration: PackedTextureMigration = {
  upscaleMethodRed: UpscaleMethod.SCALE,
  upscaleMethodGreen: UpscaleMethod.SCALE,
  upscaleMethodBlue: UpscaleMethod.SCALE,
};

export const textureMigrations: PackedTextureMigrationWithMetadata[] = [
  {
    texturePackedBaseName: "DV_Motif_Stone1Stone2Stone3_01_P",
    overrideRedName: "DV_Motif_Stone1",
    overrideGreenName: "DV_Motif_Stone2",
    overrideBlueName: "DV_Motif_Stone3",
  },
  {
    texturePackedBaseName: "DV_Grunge_SpotsCloudsNoise_01_P",
    overrideRedName: "DV_Grunge_Spots_0",
    overrideGreenName: "DV_Grunge_Clouds_1",
    overrideBlueName: "DV_Grunge_Noise_1",
  },
  {
    texturePackedBaseName: "DV_Grunge_Noise_01_P",
    overrideRedName: "DV_Grunge_Noise_White",
    upscaleMethodRed: UpscaleMethod.REPEAT,
    overrideGreenName: "DV_Grunge_Noise_1_DUPLICATE",
    replaceGreenWith: "DV_Grunge_Noise_1",
    overrideBlueName: "DV_Grunge_Noise_2",
  },
  {
    texturePackedBaseName: "DV_Grunge_Spots_01_P",
    overrideRedName: "DV_Grunge_Spots_0_DUPLICATE",
    replaceRedWith: "DV_Grunge_Spots_0",
  },
  {
    texturePackedBaseName: "DV_Grunge_Clouds_01_P",
    overrideGreenName: "DV_Grunge_Clouds_1_DUPLICATE",
    replaceGreenWith: "DV_Grunge_Clouds_1",
  },
  {
    texturePackedBaseName: "DV_Grunge_Grass_02_P",
    overrideGreenName: "DV_Grunge_Grass_Hi",
  },
];

function getMigration(packedBaseName: string): PackedTextureMigration {
  for (const migration of textureMigrations) {
    if (migration.texturePackedBaseName === packedBaseName) {
      return { ...defaultPackedTextureMigration, ...migration };
    }
  }
  return defaultPackedTextureMigration;
}

export function getTextureNameFromPackedNameAndChannel(
  packedBaseName: string,
  channelIndex: number,
  replaceWithAlternative: boolean = false,
) {
  channelIndex = Math.min(channelIndex, 2);

  if (!(packedBaseName.indexOf(".") === -1)) {
    console.assert(false);
  } // packedBaseName should not contain the file extension.

  const migration = getMigration(packedBaseName);
  const replaceNames = [migration.replaceRedWith, migration.replaceGreenWith, migration.replaceBlueWith];
  if (replaceWithAlternative && channelIndex <= 2 && replaceNames[channelIndex] !== undefined) {
    return replaceNames[channelIndex];
  }
  const overrideNames = [migration.overrideRedName, migration.overrideGreenName, migration.overrideBlueName];
  if (channelIndex <= 2 && overrideNames[channelIndex] !== undefined) {
    return overrideNames[channelIndex];
  }

  const inputIndexMatch = packedBaseName.match(/_(\d\d)_P$/);
  const inputIndex: number = inputIndexMatch ? parseInt(inputIndexMatch[1]) - 1 : 0;
  const baseName: string = packedBaseName.replace(/_\d\d_P$/, "");
  return `${baseName}_${channelIndex + inputIndex * 3}`;
}

export function getTextureUpscaleMethod(packedBaseName: string, channelIndex: number): UpscaleMethod {
  channelIndex = Math.min(channelIndex, 2);
  const migration = getMigration(packedBaseName);
  if (channelIndex === 0) {
    return migration.upscaleMethodRed;
  } else if (channelIndex === 1) {
    return migration.upscaleMethodGreen;
  } else {
    return migration.upscaleMethodBlue;
  }
}
